import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Chip,
  Avatar,
  Rating,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Breadcrumbs,
  Link,
  Skeleton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  Visibility as ViewIcon,
  GetApp as DownloadCountIcon,
  History as HistoryIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import InfoCard from '../common/cards/InfoCard';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService, uploadDirectService } from '../../services';
import type { UnifiedTag, CreateTagRequest } from '../../services';
import ShareDialog from '../shared/ShareDialog';
import type { TagScope, ContentVersion } from '../../types/content.types';
import type { StreamingMetadata } from '../../types/viewer.types';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import useTeamOverview from '../../hooks/useTeamOverview';
import { useNotification } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../utils/permissions';
import DOMPurify from 'dompurify';
import UniversalFileViewer from './UniversalFileViewer';
import TagAutoComplete from '../common/TagAutoComplete';
import { ContentMetadataDialog } from './ContentMetadataDialog';
import DeleteContentDialog from './DeleteContentDialog';
import AiSummaryModal from './AiSummaryModal';
import InlineEditField from './InlineEditField';
import VersionBadge from './VersionBadge';
import VersionHistoryPanel from './VersionHistoryPanel';
import { ArchiveContentsSection } from './ArchiveContentsSection';
import ArchivePreview from './ArchivePreview';
import { fetchArchiveEntry, ArchiveEntryError } from '../../services/archive.service';
import { getFileTypeConfig } from '../../utils/fileIconUtils';

interface ContentDetailPageProps {
  contentId: string;
  onEdit?: (content: ContentItem) => void;
  onDelete?: (content: ContentItem) => void;
  onClose?: () => void;
}

// Content item structure matching backend API
interface ContentItem {
  id: string;
  title: string;
  description: string;
  content_type?: string;
  contentType?: string;
  funnel_stage?: string;
  funnelStage?: string;
  status: string;
  author?: string;
  created_by?: string;
  organization_name?: string;
  organization?: string;
  organization_id?: string;
  organizationId?: string;
  team_name?: string;
  team?: string;
  team_id?: string;
  teamId?: string;
  thumbnail_url?: string;
  thumbnailUrl?: string;
  file_size?: string;
  fileSize?: string;
  mime_type?: string;
  mimeType?: string;
  file_name?: string;
  fileName?: string;
  archive_manifest?: any;
  archiveManifest?: any;
  avg_rating?: number;
  avgRating?: number;
  rating?: number;
  rating_count?: number;
  ratingCount?: number;
  view_count?: number;
  viewCount?: number;
  download_count?: number;
  downloadCount?: number;
  created_at?: string;
  updated_at?: string;
  personas?: string[];
  tags?: string[];
  permissions?: {
    canView?: boolean;
    canEdit?: boolean;
    canDownload?: boolean;
    canShare?: boolean;
  };
  createdAt?: Date;
  updatedAt?: Date;
  fileUrl?: string;
  metadata?: { [key: string]: any };
}

// Using UnifiedTag from api.service instead of local interface

interface ContentRating {
  id: string;
  versionNumber?: number;  // Which version was being viewed when this rating was submitted
  userId: string;
  userName: string;
  rating: number;
  comment?: string;
  createdAt: Date;
  updatedAt?: Date | null;
}

// Helper function to format file size
const formatFileSize = (fileSize?: string | number): string => {
  if (typeof fileSize === 'string') {
    // Try to parse if it's a string number
    const parsed = parseInt(fileSize, 10);
    if (!isNaN(parsed)) {
      fileSize = parsed;
    } else {
      return fileSize; // Already formatted
    }
  }
  if (typeof fileSize === 'number') {
    if (fileSize === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'K', 'M', 'G'];
    const i = Math.floor(Math.log(fileSize) / Math.log(k));
    return (fileSize / Math.pow(k, i)).toFixed(1) + sizes[i];
  }
  return 'Unknown';
};

const AI_PROCESSING_STATES = new Set([
  'uploaded',
  'metadata_extracted',
  'text_extracted',
  'ai_enrichment_pending',
]);

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={(theme) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 0', fontSize: 12,
    '&:not(:last-child)': { borderBottom: `1px solid ${theme.palette.divider}` },
  })}>
    <Box component="span" sx={(theme) => ({ color: theme.palette.text.secondary, fontSize: 12 })}>{label}</Box>
    <Box component="span" sx={(theme) => ({ color: theme.palette.text.primary, fontWeight: 600, fontSize: 12, fontVariantNumeric: 'tabular-nums' })}>{value}</Box>
  </Box>
);

const ContentDetailPage: React.FC<ContentDetailPageProps> = ({
  contentId,
  onEdit,
  onDelete,
  onClose,
}) => {
  const { currentOrganizationId, currentTeamId, organizations, teams, refreshData } = useOrganization();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { refreshOverview } = useTeamOverview(currentTeamId);
  const { showNotification } = useNotification();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ratings, setRatings] = useState<ContentRating[]>([]);
  const [ratingsPage, setRatingsPage] = useState(1);
  const [hasMoreRatings, setHasMoreRatings] = useState(true);
  const [loadingMoreRatings, setLoadingMoreRatings] = useState(false);
  const [totalRatings, setTotalRatings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [existingUserRating, setExistingUserRating] = useState<ContentRating | null>(null);
  const [isEditingReview, setIsEditingReview] = useState(false);
  const [personalTags, setPersonalTags] = useState<UnifiedTag[]>([]); // All available personal tags
  const [teamTags, setTeamTags] = useState<UnifiedTag[]>([]); // All available team tags
  const [appliedPersonalTags, setAppliedPersonalTags] = useState<UnifiedTag[]>([]); // Personal tags applied to this content
  const [appliedTeamTags, setAppliedTeamTags] = useState<UnifiedTag[]>([]); // Team tags applied to this content
  const [authorName, setAuthorName] = useState<string>('');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState<any>(undefined);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const handleOpenAiSummary = async () => {
    setMenuAnchorEl(null);
    setAiSummaryData(undefined);
    setAiSummaryLoading(true);
    setAiSummaryOpen(true);
    try {
      const fresh = await contentService.getContent(contentId, currentTeamId || '', true);
      const fetched = fresh?.content || fresh;
      setAiSummaryData(fetched?.aiSummary ?? null);
    } catch {
      setAiSummaryData(null);
    } finally {
      setAiSummaryLoading(false);
    }
  };
  const [viewingVersion, setViewingVersion] = useState<ContentVersion | null>(null);
  const [viewingVersionMetadata, setViewingVersionMetadata] = useState<StreamingMetadata | null>(null);
  const [loadingVersionView, setLoadingVersionView] = useState(false);
  const [retryingProcessing, setRetryingProcessing] = useState(false);

  // Archive entry preview state — populated when the user clicks an entry in
  // the Archive Contents listing. The object URL must be revoked when the
  // entry changes or the component unmounts to avoid leaking memory.
  const [selectedArchiveEntry, setSelectedArchiveEntry] = useState<{
    path: string;
    fileName: string;
    url: string;
    blob: Blob;
    mimeType: string;
  } | null>(null);
  const [archiveEntryLoading, setArchiveEntryLoading] = useState(false);
  const [archiveEntryError, setArchiveEntryError] = useState<{ code: string; message: string } | null>(null);

  // Stable callbacks for UniversalFileViewer — defined once so the viewer
  // doesn't see new prop references on every parent render.
  const handleViewerError = useCallback((error: any) => {
    console.error('UniversalFileViewer error:', error);
  }, []);
  const handleViewerLoad = useCallback(() => {}, []);

  // Revoke the current archive entry's object URL when it changes or on unmount.
  useEffect(() => {
    const url = selectedArchiveEntry?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedArchiveEntry?.url]);

  // Fetch and stage an archive entry for inline preview.
  const handleArchiveEntrySelect = useCallback(async (entryPath: string) => {
    if (!content) return;
    const teamId = (content as any).teamId || (content as any).team_id || currentTeamId;
    if (!teamId) {
      setArchiveEntryError({ code: 'NETWORK_ERROR', message: 'Team context required to load entry.' });
      return;
    }
    // Revoke the previous URL before staging a new fetch so we don't leak it
    // if the fetch ends up replacing the entry.
    setSelectedArchiveEntry((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setArchiveEntryLoading(true);
    setArchiveEntryError(null);
    try {
      const result = await fetchArchiveEntry(content.id, entryPath, teamId);
      setSelectedArchiveEntry({
        path: entryPath,
        fileName: result.fileName,
        url: result.url,
        blob: result.blob,
        mimeType: result.mimeType,
      });
    } catch (err) {
      if (err instanceof ArchiveEntryError) {
        setArchiveEntryError({ code: err.code, message: err.message });
      } else {
        setArchiveEntryError({
          code: 'NETWORK_ERROR',
          message: err instanceof Error ? err.message : 'Failed to load entry',
        });
      }
    } finally {
      setArchiveEntryLoading(false);
    }
  }, [content, currentTeamId]);

  // Load content function
  const loadContent = async (silent = false) => {
      // Return to current version view whenever content data reloads (not during silent background syncs)
      if (!silent) {
        setViewingVersion(null);
        setViewingVersionMetadata(null);
      }
      if (!silent) setLoading(true);
      setError(null);
      
      try {
        // Load content data fresh (bypass cache) so AI enrichment updates are always visible
        const response = await contentService.getContent(contentId, currentTeamId, true);
        
        // Extract the actual content data from the response
        // The API returns { content: {...} } structure
        const contentData = response?.content || response;
        
        if (contentData && contentData.id) {
          // Map numeric contentType to string
          const contentTypeMap: { [key: number]: string } = {
            0: 'document',
            1: 'spreadsheet',
            2: 'presentation',
            3: 'video',
            4: 'audio',
            5: 'archive',
            6: 'image',
            7: 'code',
            8: 'text'
          };
          
          // Map numeric status to string
          // Based on proto definition: 0=UNSPECIFIED, 1=DRAFT, 2=PRIVATE, 3=PUBLIC, 4=ARCHIVED
          const statusMap: { [key: number]: string } = {
            0: 'draft',
            1: 'draft',
            2: 'private',
            3: 'public',
            4: 'archived'
          };

          // Determine content type - handle both numeric and string types
          let contentType = 'document';
          if (typeof contentData.contentType === 'number') {
            contentType = contentTypeMap[contentData.contentType] || 'document';
          } else if (typeof contentData.contentType === 'string') {
            contentType = contentData.contentType;
          }

          // Determine status - handle both numeric and string types
          let status = 'private';
          if (typeof contentData.status === 'number') {
            status = statusMap[contentData.status] || 'private';
          } else if (typeof contentData.status === 'string') {
            status = contentData.status;
          }
          
          // Normalize the content data structure - NO FALLBACKS, only use actual data
          const normalizedContent: ContentItem = {
            id: contentData.id,
            title: contentData.title,
            description: contentData.description,
            contentType: contentType,
            funnelStage: contentData.funnelStage, // Don't add fallbacks for fields that don't exist
            status: status,
            author: contentData.author,
            organization: contentData.organization,
            organizationId: contentData.organizationId,
            team: contentData.team,
            teamId: contentData.teamId,
            thumbnailUrl: contentData.thumbnailUrl,
            fileSize: contentData.fileSize,
            mimeType: contentData.mimeType,
            avgRating: contentData.rating || 0,
            ratingCount: contentData.ratingCount || 0,
            viewCount: contentData.viewCount || 0,
            downloadCount: contentData.downloadCount || 0,
            createdAt: contentData.createdAt ? new Date(contentData.createdAt) : new Date(),
            updatedAt: contentData.updatedAt ? new Date(contentData.updatedAt) : new Date(),
            personas: contentData.personas || [],
            tags: contentData.tags || [],
            permissions: {
              canView: contentData.permissions?.canView ?? true,
              canEdit: contentData.permissions?.canEdit ?? false,
              canDownload: contentData.permissions?.canDownload ?? true,
              canShare: contentData.permissions?.canShare ?? true,
            },
            currentVersionNumber: contentData.currentVersionNumber ?? 0,
            totalVersionCount: contentData.totalVersionCount,
            processingState: contentData.processingState,
            isFullySearchable: contentData.isFullySearchable,
            aiSuggestedTitle: contentData.aiSuggestedTitle,
            aiDescription: contentData.aiDescription,
            descriptionModel: contentData.descriptionModel,
            aiSummary: contentData.aiSummary,
            suggestedTags: contentData.suggestedTags || [],
            archiveManifest: contentData.archiveManifest,
          } as any;
          
          setContent(normalizedContent);

          // Resolve author display name synchronously from what we already know
          const authorId = contentData.author;
          if (!authorId || authorId === 'anonymous-user') {
            setAuthorName(authorId === 'anonymous-user' ? 'Anonymous User' : '');
          } else if (authorId.includes('@')) {
            setAuthorName(authorId);
          } else if (!/^[0-9a-fA-F]{24}$/.test(authorId)) {
            setAuthorName(authorId);
          }
          // ObjectId author names are resolved in the parallel fetch below

          const orgId = contentData.organization_id || currentOrganizationId;
          const tId = contentData.team_id || currentTeamId;
          const isValidObjectId = /^[0-9a-fA-F]{24}$/.test(authorId);

          // Fire all independent secondary fetches in parallel
          const [
            authorResult,
            personalTagsResult,
            teamTagsResult,
            ratingsResult,
            bookmarkResult,
            userRatingResult,
          ] = await Promise.allSettled([
            // 1. Author name (only when we have a real ObjectId)
            isValidObjectId
              ? userService.getUserById(authorId)
              : Promise.resolve(null),
            // 2. Personal tags
            orgId && tId
              ? tagService.listUnifiedTags({ tag_type: 'personal', organization_id: orgId, team_id: tId, limit: 100 })
              : Promise.resolve([]),
            // 3. Team tags
            orgId && tId
              ? tagService.listUnifiedTags({ tag_type: 'organizational', organization_id: orgId, team_id: tId, limit: 100 })
              : Promise.resolve([]),
            // 4. Ratings
            contentService.getContentRatingsPaginated(contentId, { page: 1, limit: 10 }),
            // 5. Bookmark status
            contentService.isContentBookmarked(contentId),
            // 6. User's own rating
            contentService.getUserContentRating(contentId),
          ]);

          // Apply author result
          if (isValidObjectId) {
            if (authorResult.status === 'fulfilled' && authorResult.value) {
              const userData = authorResult.value as any;
              setAuthorName(userData.name || userData.email || `User ${authorId}`);
            } else {
              setAuthorName(`User ${authorId}`);
            }
          }

          // Apply personal tags
          if (personalTagsResult.status === 'fulfilled') {
            setPersonalTags((personalTagsResult.value as any[] || []).filter((tag: any) => tag.scope === 'personal'));
          } else {
            setPersonalTags([]);
          }

          // Apply team tags
          if (teamTagsResult.status === 'fulfilled') {
            setTeamTags((teamTagsResult.value as any[] || []).filter((tag: any) => tag.scope === 'team'));
          } else {
            setTeamTags([]);
          }

          // Apply ratings
          if (ratingsResult.status === 'fulfilled') {
            const response = ratingsResult.value as any;
            const ratingsData = response.data || response.ratings || [];
            const filteredReviews = ratingsData.filter((review: any) =>
              (review.userId || review.user_id) !== user?._id
            );
            const transformedRatings = filteredReviews.map((review: any) => {
              const createdValue = review.createdAt || review.created_at;
              const updatedValue = review.updatedAt || review.updated_at;
              return {
                id: review.id,
                versionNumber: review.versionNumber ?? 0,
                userId: review.userId || review.user_id,
                userName: review.userName || review.user_name || `User ${(review.userId || review.user_id || '').slice(-8)}` || 'Anonymous User',
                rating: review.rating,
                comment: review.comment || '',
                createdAt: createdValue ? new Date(createdValue) : new Date('invalid'),
                updatedAt: updatedValue ? new Date(updatedValue) : null
              };
            });
            setRatings(transformedRatings);
            const paginationData = response.metadata?.pagination || response.pagination || {};
            setTotalRatings(paginationData.totalCount || 0);
            setHasMoreRatings(paginationData.hasNextPage || false);
            setRatingsPage(1);
          } else {
            setRatings([]);
            setHasMoreRatings(false);
          }

          // Apply bookmark status
          if (bookmarkResult.status === 'fulfilled') {
            setBookmarked((bookmarkResult.value as any).is_bookmarked);
          } else {
            setBookmarked(false);
          }

          // Apply user's own rating
          if (userRatingResult.status === 'fulfilled' && userRatingResult.value) {
            const userRatingData = userRatingResult.value as any;
            const createdValue = userRatingData.createdAt || userRatingData.created_at;
            const updatedValue = userRatingData.updatedAt || userRatingData.updated_at;
            setExistingUserRating({
              id: userRatingData.id,
              versionNumber: userRatingData.versionNumber ?? 0,
              userId: userRatingData.user_id || userRatingData.userId,
              userName: 'You',
              rating: userRatingData.rating,
              comment: userRatingData.comment || '',
              createdAt: createdValue ? new Date(createdValue) : new Date('invalid'),
              updatedAt: updatedValue ? new Date(updatedValue) : null
            });
          } else {
            setExistingUserRating(null);
          }
        } else {
          setError('Content not found');
        }
        
      } catch (error: any) {
        console.error('Error loading content:', error);
        if (!silent) setError(error?.message || 'Failed to load content');
      } finally {
        if (!silent) setLoading(false);
      }
  };

  // Load content on mount and when the content ID changes.
  // Deliberately excludes organizations/teams — name resolution is done in a
  // separate effect below so that React Query background-refetches of those
  // arrays don't trigger a full content reload.
  useEffect(() => {
    loadContent();
  }, [contentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent poll: fetch processingState without touching loading/version state.
  // Returns the same state reference if nothing changed — no re-render, no flash.
  const pollEnrichmentState = useCallback(async () => {
    try {
      const response = await contentService.getContent(contentId, currentTeamId || '', true);
      const fetched = response?.content || response;
      if (!fetched) return;
      setContent(prev => {
        if (!prev) return prev;
        if (fetched.processingState === (prev as any).processingState) return prev;
        return {
          ...prev,
          processingState: fetched.processingState,
          isFullySearchable: fetched.isFullySearchable ?? (prev as any).isFullySearchable,
          description: fetched.description ?? prev.description,
          aiDescription: fetched.aiDescription ?? (prev as any).aiDescription,
          aiSuggestedTitle: fetched.aiSuggestedTitle ?? (prev as any).aiSuggestedTitle,
          descriptionModel: fetched.descriptionModel ?? (prev as any).descriptionModel,
          aiSummary: fetched.aiSummary ?? (prev as any).aiSummary,
          suggestedTags: fetched.suggestedTags ?? (prev as any).suggestedTags,
          tags: fetched.tags ?? (prev as any).tags,
        };
      });
    } catch {
      // silently ignore — next tick will retry
    }
  }, [contentId, currentTeamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for AI enrichment completion while the current version is still processing.
  // Only runs when viewing the live version (not a historical snapshot).
  useEffect(() => {
    const processingState = (content as any)?.processingState;
    if (viewingVersion || !AI_PROCESSING_STATES.has(processingState ?? '')) return;
    const id = setInterval(pollEnrichmentState, 5000);
    return () => clearInterval(id);
  }, [(content as any)?.processingState, viewingVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve org/team display names from the cached lists whenever the content
  // or the lists themselves change (e.g. first load or a true data update).
  useEffect(() => {
    if (!content) return;
    const orgId = (content as any).organization_id || content.organizationId;
    if (orgId) {
      const org = organizations.find(o => o.id === orgId);
      setOrganizationName(org?.name || 'Unknown Organization');
    }
    const tId = (content as any).team_id || content.teamId;
    if (tId) {
      const team = teams.find(t => t.id === tId);
      setTeamName(team?.name || 'Unknown Team');
    }
  }, [content, organizations, teams]);

  // Filter tags to show only those applied to this content
  useEffect(() => {
    if (content && content.tags && personalTags.length > 0) {
      const contentTagIds = content.tags || [];
      // Filter for personal tags that are applied to this content
      // Check scope to ensure it's a personal tag
      const filteredPersonalTags = personalTags.filter(tag => 
        contentTagIds.includes(tag.id) && 
        tag.scope === 'personal'
      );
      setAppliedPersonalTags(filteredPersonalTags);
    }
  }, [content, personalTags]);

  useEffect(() => {
    if (content && content.tags && teamTags.length > 0) {
      const contentTagIds = content.tags || [];
      // Filter for team tags that are applied to this content
      // Check scope to ensure it's a team tag
      const filteredTeamTags = teamTags.filter(tag => 
        contentTagIds.includes(tag.id) && 
        tag.scope === 'team'
      );
      setAppliedTeamTags(filteredTeamTags);
    }
  }, [content, teamTags]);

  const handleRetryProcessing = async () => {
    if (!content || retryingProcessing) return;
    setRetryingProcessing(true);
    try {
      await contentService.retryProcessing(content.id, currentTeamId || '');
      showNotification('AI processing re-queued', 'success');
      // Refresh content to see updated processingState
      await loadContent();
    } catch (err: any) {
      showNotification(err?.message || 'Failed to retry processing', 'error');
    } finally {
      setRetryingProcessing(false);
    }
  };

  const handleAction = (action: string) => {
    if (!content) return;

    switch (action) {
      case 'download':
        handleDownload();
        break;
      case 'share':
        // Only allow sharing PUBLIC content
        if (content.status !== 'public') {
          showNotification(
            'Only public content can be shared. Please change the content status to "Public" first.',
            'warning'
          );
          break;
        }
        setShareDialogOpen(true);
        break;
      case 'bookmark':
        const toggleBookmark = async () => {
          try {
            if (bookmarked) {
              // Remove bookmark
              const bookmarkStatus = await contentService.isContentBookmarked(contentId);
              if (bookmarkStatus.is_bookmarked && bookmarkStatus.bookmark_id) {
                await contentService.deleteBookmark(bookmarkStatus.bookmark_id);
                setBookmarked(false);
                showNotification('Bookmark removed successfully', 'success');
              } else {
                showNotification('Could not find bookmark to remove', 'warning');
              }
            } else {
              // Create bookmark
              if (currentOrganizationId) {
                console.log('Creating bookmark for content:', contentId);
                const result = await contentService.createBookmark({
                  content_id: contentId,
                  organization_id: currentOrganizationId,
                  team_id: currentTeamId,
                  priority: 2 // MEDIUM priority
                });
                setBookmarked(true);
                showNotification('Bookmark created successfully', 'success');
              } else {
                console.error('Organization context required for bookmarking');
                showNotification('Organization context required for bookmarking', 'error');
              }
            }
          } catch (error) {
            console.error('Error updating bookmark:', error);
            showNotification(`Failed to update bookmark: ${(error as Error).message}`, 'error');
          }
        };
        toggleBookmark();
        break;
      case 'edit':
        // If viewing a historical version, return to current before opening the edit dialog
        // so the user always edits what they're looking at.
        if (viewingVersion) {
          handleVersionSelect(null);
        }
        setShowEditDialog(true);
        onEdit?.(content); // Keep backward compatibility
        break;
      case 'delete':
        setShowDeleteDialog(true);
        onDelete?.(content); // Keep backward compatibility
        break;
    }
    setMenuAnchorEl(null);
  };

  const handleOpenReviewDialog = () => {
    if (existingUserRating) {
      // Pre-populate with existing review data
      setUserRating(existingUserRating.rating);
      setUserComment(existingUserRating.comment || '');
      setIsEditingReview(true);
      console.log('Opening review dialog for editing existing review:', existingUserRating);
    } else {
      // Start fresh for new review
      setUserRating(0);
      setUserComment('');
      setIsEditingReview(false);
      console.log('Opening review dialog for new review');
    }
    setShowRatingDialog(true);
  };

  // Handler for applying a personal tag to content
  const handleApplyPersonalTag = async (tagId: string, tag: UnifiedTag) => {
    if (!content) {
      console.error('No content available');
      alert('Content not loaded. Please refresh and try again.');
      return;
    }

    try {
      console.log('Applying personal tag to content:', tag.name);

      // Check if tag is already applied
      const currentTagIds = content.tags || [];
      if (!currentTagIds.includes(tagId)) {
        // Use the new dedicated endpoint for applying tags
        const teamId = content.teamId || content.team_id;
        if (!teamId) {
          throw new Error('Team ID is required to apply tags');
        }
        const result = await contentService.applyTagsToContent(content.id, [tagId], teamId);

        console.log('Tag applied to content successfully:', result.message);

        // Efficiently update only the content.tags array
        setContent(prev => prev ? {
          ...prev,
          tags: [...(prev.tags || []), tagId]
        } : null);

        // Only add to personalTags if it doesn't exist yet (avoid duplicates)
        // This prevents adding the tag twice when creating a new tag and immediately applying it
        setPersonalTags(prev => {
          const exists = prev.some(t => t.id === tagId);
          if (!exists && tag.scope === 'personal') {
            return [...prev, tag];
          }
          return prev;
        });
      } else {
        console.log('Tag already applied to this content');
      }
    } catch (error: any) {
      console.error('Failed to apply personal tag:', error);
      alert(`Failed to apply tag: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  // Handler for removing a personal tag from content
  const handleRemovePersonalTag = async (tagId: string) => {
    if (!content) return;

    try {
      console.log('Removing personal tag from content:', tagId);
      
      // Use the new dedicated endpoint for removing tags
      const teamId = content.teamId || content.team_id;
      if (!teamId) {
        throw new Error('Team ID is required to remove tags');
      }
      const result = await contentService.removeTagsFromContent(content.id, [tagId], teamId);
      
      console.log('Tag removed from content successfully:', result.message);
      
      // Efficiently update only the content.tags array
      setContent(prev => prev ? {
        ...prev,
        tags: (prev.tags || []).filter(id => id !== tagId)
      } : null);
    } catch (error) {
      console.error('Error removing personal tag:', error);
      alert(`Failed to remove tag: ${(error as any).response?.data?.message || (error as Error).message || 'Unknown error'}`);
    }
  };

  // Handler for creating new personal tags
  const handleCreatePersonalTag = async (request: CreateTagRequest): Promise<UnifiedTag> => {
    try {
      console.log('Creating new personal tag:', request);
      
      const newTag = await tagService.createUnifiedTag(request);
      console.log('Created new tag:', newTag);
      
      // Add the new tag to the personalTags list
      setPersonalTags(prev => [...prev, newTag]);
      
      return newTag;
    } catch (error: any) {
      console.error('Failed to create personal tag:', error);
      throw error; // Re-throw to let TagAutoComplete handle the error display
    }
  };

  const handleLoadMoreRatings = async () => {
    if (!hasMoreRatings || loadingMoreRatings) return;

    setLoadingMoreRatings(true);
    try {
      console.log('Loading more ratings, page:', ratingsPage + 1);

      const response = await contentService.getContentRatingsPaginated(
        contentId,
        { page: ratingsPage + 1, limit: 10, sort_by: 'date' }
      );

      const ratingsData = response.data || response.ratings || [];
      const filteredRatings = ratingsData.filter((review: any) =>
        (review.userId || review.user_id) !== user?._id
      );
      const transformedRatings = filteredRatings.map((review: any) => {
        const createdValue = review.createdAt || review.created_at;
        const updatedValue = review.updatedAt || review.updated_at;
        return {
          id: review.id,
          versionNumber: review.versionNumber ?? 0,
          userId: review.userId || review.user_id,
          userName: review.userName || review.user_name || `User ${(review.userId || review.user_id || '').slice(-8)}`,
          rating: review.rating,
          comment: review.comment || '',
          createdAt: createdValue ? new Date(createdValue) : new Date('invalid'),
          updatedAt: updatedValue ? new Date(updatedValue) : null
        };
      });

      setRatings(prev => [...prev, ...transformedRatings]);
      setRatingsPage(prev => prev + 1);
      // The API returns pagination in response.metadata.pagination
      const paginationData = response.metadata?.pagination || response.pagination || {};
      setHasMoreRatings(paginationData.hasNextPage || false);

      console.log('Loaded', transformedRatings.length, 'more ratings');
    } catch (error) {
      console.error('Error loading more ratings:', error);
      setHasMoreRatings(false);
    } finally {
      setLoadingMoreRatings(false);
    }
  };

  const handleDownload = async () => {
    if (!content) return;

    const teamId = content.teamId || content.team_id || currentTeamId;
    if (!teamId) {
      console.error('No team ID available for download');
      alert('Unable to download: team context required');
      return;
    }

    try {
      console.log('Starting Phase 2.5 direct download for:', content.title, 'teamId:', teamId);

      // Use blob download method to force actual download behavior
      const { blob, fileName } = await uploadDirectService.downloadFileAsBlob(content.id, teamId);

      // Create object URL from blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element and trigger direct download
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || content.title;

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      window.URL.revokeObjectURL(url);

      console.log('Phase 2.5 direct download initiated successfully');

    } catch (error) {
      console.error('Phase 2.5 direct download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };


  const handleRatingSubmit = async () => {
    if (!content || userRating === 0) return;

    try {
      // Use the same endpoint for both create and update
      console.log(isEditingReview ? 'Updating existing rating' : 'Submitting new rating', 'with rating:', userRating, 'and comment:', userComment);
      
      // The API endpoint handles both create and update based on whether the user has already rated
      const effectiveVersionNumber = viewingVersion?.versionNumber ?? (content as any).currentVersionNumber ?? 0;
      const ratingResult = await contentService.rateContentWithComment(
        contentId,
        userRating,
        content.teamId || content.team_id || currentTeamId,
        userComment || undefined,
        effectiveVersionNumber
      );
      
      console.log('Rating', isEditingReview ? 'updated' : 'submitted', 'successfully:', ratingResult);
      
      // Update rating display — update whichever record is currently being viewed
      if (viewingVersion) {
        setViewingVersion(prev => prev ? {
          ...prev,
          avgRating: ratingResult.averageRating ?? prev.avgRating,
          ratingCount: ratingResult.totalRatings ?? prev.ratingCount ?? 0,
        } : null);
      } else {
        setContent(prev => prev ? {
          ...prev,
          avgRating: ratingResult.averageRating || prev.avgRating,
          ratingCount: ratingResult.totalRatings || prev.ratingCount || 0,
          // Preserve date objects
          createdAt: prev.createdAt instanceof Date ? prev.createdAt : new Date(prev.createdAt),
          updatedAt: prev.updatedAt instanceof Date ? prev.updatedAt : new Date(prev.updatedAt)
        } : null);
      }
      
      // Update or set the existing user rating
      setExistingUserRating({
        id: existingUserRating?.id || `rating-${Date.now()}`,
        versionNumber: effectiveVersionNumber,
        userId: user?._id || '',
        userName: 'You',
        rating: userRating,
        comment: userComment || '',
        createdAt: existingUserRating?.createdAt || new Date()
      });
      
      // Reload all ratings to show updated data
      try {
        const response = await contentService.getContentRatingsPaginated(contentId, { page: 1, limit: 10 });
        const ratingsData = response.data || response.ratings || [];
        const filteredReviews = ratingsData.filter((review: any) =>
          (review.userId || review.user_id) !== user?._id
        );
        const transformedRatings = filteredReviews.map((review: any) => {
          const createdValue = review.createdAt || review.created_at;
          const updatedValue = review.updatedAt || review.updated_at;
          return {
            id: review.id,
            versionNumber: review.versionNumber ?? 0,
            userId: review.userId || review.user_id,
            userName: review.userName || review.user_name || `User ${(review.userId || review.user_id || '').slice(-8)}`,
            rating: review.rating,
            comment: review.comment || '',
            createdAt: createdValue ? new Date(createdValue) : new Date('invalid'),
            updatedAt: updatedValue ? new Date(updatedValue) : null
          };
        });
        setRatings(transformedRatings);
        const paginationData = response.metadata?.pagination || response.pagination || {};
        setTotalRatings(paginationData.totalCount || 0);
        setHasMoreRatings(paginationData.hasNextPage || false);
        setRatingsPage(1);
      } catch (reloadError) {
        console.log('Could not reload ratings after submission:', reloadError);
      }
      
      // Reset form
      setShowRatingDialog(false);
      setUserRating(0);
      setUserComment('');
      
    } catch (error) {
      console.error('Error submitting rating:', error);
      // Show error to user - you might want to add a snackbar or alert here
      alert('Failed to submit rating. Please try again.');
    }
  };

  // Inline save handlers for title/description
  const handleVersionSelect = async (versionNumber: number | null) => {
    if (!content) return;
    if (versionNumber === null) {
      // Return to current version — ratings are already loaded (all ratings, no version filter)
      setViewingVersion(null);
      setViewingVersionMetadata(null);
      return;
    }
    setLoadingVersionView(true);
    try {
      const teamId = content.teamId || (content as any).team_id || currentTeamId || '';
      const [versionData, urlResult] = await Promise.all([
        contentService.getVersion(content.id, versionNumber, teamId),
        uploadDirectService.getVersionPresignedUrl(content.id, versionNumber, teamId),
      ]);
      const mimeType = versionData?.mimeType || 'application/octet-stream';
      const meta: StreamingMetadata = {
        contentId: content.id,
        fileName: versionData?.titleAtPublish || content.title || 'file',
        mimeType,
        fileSize: versionData?.fileSizeBytes || 0,
        contentType: mimeType.startsWith('image/') ? 'image'
          : mimeType.startsWith('video/') ? 'video'
          : mimeType.startsWith('audio/') ? 'audio'
          : mimeType === 'application/pdf' ? 'document'
          : 'unknown',
        canStream: false,
        canConvert: true,
        availableFormats: ['pdf'],
        presignedUrl: urlResult?.presigned_url,
        downloadStrategy: 'direct',
      };
      setViewingVersion(versionData);
      setViewingVersionMetadata(meta);
      // Ratings remain unchanged — all ratings are always shown regardless of version
    } catch (err: any) {
      console.error('Failed to load version:', err);
    } finally {
      setLoadingVersionView(false);
    }
  };

  const handleInlineTitleSave = async (newTitle: string) => {
    if (!content) return;
    await contentService.updateContent(content.id, {
      title: newTitle.trim(),
      team_id: content.teamId || content.team_id || currentTeamId,
    } as any);
    await loadContent();
  };

  const handleInlineDescriptionSave = async (newDescription: string) => {
    if (!content) return;
    await contentService.updateContent(content.id, {
      description: newDescription,
      team_id: content.teamId || content.team_id || currentTeamId,
    } as any);
    await loadContent();
  };

  // Handler for saving content edits
  const handleSaveContentEdits = async (updates: {
    title: string;
    description: string;
    status?: string;
    tag_ids: string[];
  }) => {
    if (!content) return;

    try {
      console.log('Saving content updates:', updates);

      const updateData: any = {
        title: updates.title,
        description: updates.description,
        tags: updates.tag_ids,
        team_id: content.teamId || content.team_id || currentTeamId,
      };

      // Include status if provided
      if (updates.status) {
        updateData.status = updates.status;
      }

      await contentService.updateContent(content.id, updateData);

      console.log('Content updated successfully');

      // Silent reload — keep dialog mounted (setLoading would unmount it).
      // The useEffect on [content, teamTags] recomputes appliedTeamTags after reload.
      await loadContent(true);
    } catch (error: any) {
      console.error('Error updating content:', error);
      alert(`Failed to update content: ${error.response?.data?.message || error.message || 'Unknown error'}`);
      throw error; // Re-throw to let dialog handle the error state
    }
  };

  // Handler for deleting content
  const handleDeleteContent = async () => {
    if (!content) return;

    const teamId = content.teamId || content.team_id || currentTeamId;
    if (!teamId) {
      throw new Error('Team ID is required to delete content');
    }

    try {
      console.log('Deleting content:', content.id);

      await contentService.deleteContent(content.id, teamId);

      console.log('Content deleted successfully');

      // Show success notification
      showNotification('Content deleted successfully', 'success');

      // Navigate away immediately to prevent useEffect from trying to reload deleted content
      // The content list page will refresh data automatically and show updated statistics
      if (onClose) {
        onClose();
      } else {
        window.location.href = '/content';
      }
    } catch (error: any) {
      console.error('Error deleting content:', error);
      showNotification(
        `Failed to delete content: ${error.response?.data?.error?.message || error.response?.data?.message || error.message || 'Unknown error'}`,
        'error'
      );
      throw error; // Re-throw to let dialog handle the error state
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="rectangular" height={300} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={100} sx={{ mb: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Content
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!content) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Content Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            The requested content could not be found or you don't have permission to view it.
          </Typography>
          <Button variant="outlined" href="/content">
            Back to Content Library
          </Button>
        </Paper>
      </Container>
    );
  }

  // Helper function to safely format dates
  const formatDateSafely = (date: Date | string | null | undefined): string => {
    if (!date) return 'Missing Date';
    
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        return 'Invalid Date';
      }
      return dateObj.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  // Helper function to format created/updated dates for ratings
  const formatRatingDates = (createdAt: Date, updatedAt?: Date | null): string => {
    const createdStr = formatDateSafely(createdAt);
    
    if (!updatedAt || isNaN(updatedAt.getTime())) {
      return createdStr;
    }
    
    // Check if updated date is significantly different from created date (more than 1 minute)
    const timeDiff = Math.abs(updatedAt.getTime() - createdAt.getTime());
    const oneMinute = 60 * 1000;
    
    if (timeDiff > oneMinute) {
      const updatedStr = formatDateSafely(updatedAt);
      return `${createdStr}, updated on ${updatedStr}`;
    }
    
    return createdStr;
  };

  // Helper function to get human-readable content type from numeric code or mime type
  const getHumanReadableContentType = (content: ContentItem): string => {
    // First check mime type if available
    const mimeType = content.mimeType || content.mime_type;
    if (mimeType) {
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType === 'application/pdf') return 'pdf';
      if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation';
      if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
    }
    
    // Map numeric content types to human-readable strings
    const contentTypeMap: Record<number, string> = {
      0: 'document',
      1: 'image',
      2: 'video',
      3: 'pdf',
      4: 'presentation',
      5: 'brochure',
      6: 'case-study',
      7: 'spreadsheet',
      8: 'audio'
    };
    
    const contentType = content.contentType || content.content_type;
    
    if (typeof contentType === 'number') {
      return contentTypeMap[contentType] || 'document';
    }
    
    // If already a string, return as is
    return contentType || 'document';
  };

  // Helper function to get human-readable status
  const getHumanReadableStatus = (status: string | number): string => {
    // Map numeric status codes to human-readable text
    // Based on proto definition: 0=UNSPECIFIED, 1=DRAFT, 2=PRIVATE, 3=PUBLIC, 4=ARCHIVED
    const statusMap: Record<number, string> = {
      0: 'draft',
      1: 'draft',
      2: 'private',
      3: 'public',
      4: 'archived'
    };

    if (typeof status === 'number') {
      return statusMap[status] || 'unknown';
    }

    // If already a string, return as is
    return status || 'unknown';
  };

  const getContentTypeConfig = (content: ContentItem) => {
    const mimeType = content.mimeType || content.mime_type;
    const fileName = content.title; // Use title as filename fallback
    return getFileTypeConfig(mimeType, fileName);
  };
  
  const humanReadableContentType = getHumanReadableContentType(content);
  const humanReadableStatus = getHumanReadableStatus(content.status);
  const contentTypeConfig = getContentTypeConfig(content);

  // Computed values that switch between live data and version snapshot
  const isEnrichmentPending = !viewingVersion && AI_PROCESSING_STATES.has((content as any).processingState ?? '');
  const canEditContent = !viewingVersion && hasPermission(PERMISSIONS.CONTENT_UPDATE);
  const displayTitle = viewingVersion?.titleAtPublish ?? content.title;
  const displayDescription = viewingVersion?.descriptionAtPublish ?? content.description;
  const displayViewCount = viewingVersion?.viewCount ?? content.viewCount;
  const displayDownloadCount = viewingVersion?.downloadCount ?? content.downloadCount;
  // Compute fallback average from loaded ratings when content record has stale zeros (DB not yet updated)
  const allLoadedRatings = [...ratings, ...(existingUserRating ? [existingUserRating] : [])];
  const computedAvgRating = allLoadedRatings.length > 0
    ? allLoadedRatings.reduce((sum, r) => sum + r.rating, 0) / allLoadedRatings.length
    : 0;
  const displayAvgRating = viewingVersion?.avgRating ?? ((content.avgRating ?? 0) > 0 ? content.avgRating : computedAvgRating);
  const displayRatingCount = viewingVersion?.ratingCount ?? (content.ratingCount > 0 ? content.ratingCount : (totalRatings || allLoadedRatings.length || 0));
  const displayFileSize = viewingVersion?.fileSizeBytes || content.fileSize;

  const archiveMimes = new Set([
    'application/zip',
    'application/x-zip-compressed',
    'application/x-tar',
    'application/gzip',
    'application/x-gzip',
  ]);
  const contentMime = (content?.mime_type ?? (content as any)?.mimeType ?? '').toLowerCase();
  const contentName = (content?.file_name ?? (content as any)?.fileName ?? content?.title ?? '').toLowerCase();
  const isArchive = archiveMimes.has(contentMime) || /\.(zip|tar|tar\.gz|tgz)$/.test(contentName);
  const archiveManifest = (content as any)?.archive_manifest ?? (content as any)?.archiveManifest;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Version view banner */}
      {viewingVersion && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => handleVersionSelect(null)}
              disabled={loadingVersionView}
            >
              Return to current
            </Button>
          }
        >
          {loadingVersionView
            ? 'Loading version…'
            : `Viewing historical version v${viewingVersion.versionNumber}${viewingVersion.publishedAt ? ` — published ${new Date(viewingVersion.publishedAt).toLocaleDateString()}` : ''}`}
        </Alert>
      )}
      {loadingVersionView && !viewingVersion && (
        <Alert severity="info" sx={{ mb: 2 }}>Loading version…</Alert>
      )}

      {/* Back link */}
      <Box
        component="span"
        onClick={() => onClose ? onClose() : undefined}
        sx={(theme) => ({
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          fontSize: 12, fontWeight: 600,
          color: theme.palette.text.secondary,
          cursor: 'pointer',
          padding: '6px 10px 6px 6px', borderRadius: '8px',
          marginBottom: 1.75,
          '&:hover': { background: theme.palette.action.hover, color: theme.palette.text.primary },
        })}
      >
        <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to Content Library
      </Box>

      {/* Heading row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, marginTop: 2, marginBottom: 1.75 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
            <Chip label={contentTypeConfig.label} color="primary" size="small" />
            <Chip
              label={humanReadableStatus}
              color={
                humanReadableStatus === 'public' ? 'success' :
                humanReadableStatus === 'private' ? 'info' :
                humanReadableStatus === 'archived' ? 'warning' :
                'default'
              }
              size="small"
            />
            <VersionBadge
              currentVersionNumber={(content as any).currentVersionNumber ?? 0}
              totalVersionCount={(content as any).totalVersionCount}
              onClick={() => setVersionHistoryOpen(true)}
              isPublisher={hasPermission(PERMISSIONS.CONTENT_UPDATE)}
            />
            {isEnrichmentPending && (
              <Chip
                icon={<AutoAwesomeIcon fontSize="small" />}
                label="AI Processing"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <InlineEditField
              value={displayTitle}
              onSave={handleInlineTitleSave}
              canEdit={canEditContent}
              type="text"
              placeholder="Enter a title..."
              typographyVariant="h4"
            />
          </Box>

          {isEnrichmentPending && (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              action={
                canEditContent ? (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={handleRetryProcessing}
                    disabled={retryingProcessing}
                    startIcon={retryingProcessing ? <CircularProgress size={14} /> : undefined}
                  >
                    {retryingProcessing ? 'Retrying…' : 'Retry'}
                  </Button>
                ) : undefined
              }
            >
              AI enrichment is in progress. Open Edit Content when processing completes to review and selectively apply the AI suggestions.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <InlineEditField
              value={displayDescription}
              onSave={handleInlineDescriptionSave}
              canEdit={canEditContent}
              type="richtext"
              placeholder="Add a description..."
              typographyVariant="body1"
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, pt: 0.5 }}>
          {hasPermission(PERMISSIONS.CONTENT_DOWNLOAD) && (
            <Button variant="contained" size="small" onClick={() => handleAction('download')} startIcon={<DownloadIcon />}>
              Download
            </Button>
          )}
          {hasPermission(PERMISSIONS.CONTENT_SHARE) && (
            <Button variant="outlined" size="small" onClick={() => handleAction('share')} startIcon={<ShareIcon />}>
              Share
            </Button>
          )}
          {hasPermission(PERMISSIONS.CONTENT_UPDATE) && (
            <Button variant="outlined" size="small" onClick={() => handleAction('edit')} startIcon={<EditIcon />}>
              Edit
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenAiSummary}
            startIcon={<AutoAwesomeIcon />}
          >
            AI Summary
          </Button>
          <IconButton size="small" onClick={() => handleAction('bookmark')}>
            {bookmarked ? <BookmarkIcon color="primary" fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={(e) => setMenuAnchorEl(e.currentTarget)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* File Viewer Section */}
      <Box sx={{ mb: 3 }}>
        {isArchive ? (
          <ArchivePreview
            archiveManifest={archiveManifest}
            selectedEntry={selectedArchiveEntry}
            entryLoading={archiveEntryLoading}
            entryError={archiveEntryError}
            onDownloadArchive={handleDownload}
          />
        ) : (
          <UniversalFileViewer
            contentId={contentId}
            teamId={currentTeamId}
            displayName={displayTitle}
            initialMetadata={viewingVersionMetadata ?? undefined}
            height={600}
            width="100%"
            showToolbar={true}
            showDownload={false}
            showFullscreen={true}
            onError={handleViewerError}
            onLoad={handleViewerLoad}
          />
        )}
      </Box>

      {/* Archive Contents — directly under the preview, since browsing
          inside the archive is part of the file viewing experience, not
          metadata about the file. */}
      {isArchive && (
        <Box sx={{ mb: 3 }}>
          <ArchiveContentsSection
            manifest={archiveManifest}
            selectedEntryPath={selectedArchiveEntry?.path}
            onEntrySelect={handleArchiveEntrySelect}
            interactive={['zip', 'tar', 'tar.gz'].includes((archiveManifest?.format ?? '').toLowerCase())}
          />
        </Box>
      )}

      {/* Overview + Engagement Info Cards — side by side */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <InfoCard title="Overview" icon={<InfoIcon sx={{ fontSize: 16 }} />}>
            <InfoRow label="Status" value={humanReadableStatus} />
            <InfoRow label="File type" value={humanReadableContentType} />
            <InfoRow label="Size" value={formatFileSize(displayFileSize)} />
            <InfoRow label="Author" value={authorName || content.author || 'Unknown'} />
            <InfoRow label="Organization" value={organizationName || content.organization || 'Unknown'} />
            <InfoRow label="Team" value={teamName || content.team || 'Unknown'} />
            {viewingVersion ? (
              <InfoRow
                label="Version published"
                value={viewingVersion.publishedAt ? new Date(viewingVersion.publishedAt).toLocaleDateString() : 'Unknown'}
              />
            ) : (
              <>
                <InfoRow label="Uploaded" value={content.createdAt?.toLocaleDateString() || 'Unknown'} />
                <InfoRow label="Last modified" value={content.updatedAt?.toLocaleDateString() || 'Unknown'} />
              </>
            )}
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title="Engagement" icon={<BarChartIcon sx={{ fontSize: 16 }} />}>
            <InfoRow label="Rating" value={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating value={displayAvgRating} precision={0.1} size="small" readOnly />
                <span>{displayAvgRating.toFixed(1)} ({displayRatingCount})</span>
              </Box>
            } />
            <InfoRow label="Views" value={displayViewCount} />
            <InfoRow label="Downloads" value={displayDownloadCount} />
          </InfoCard>
        </Grid>
      </Grid>

      {/* Tags and Reviews Section - Side by Side */}
      <Grid container spacing={3}>
        {/* Tags Section - Left Side */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>

            {/* Team Tags (from content owner) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                Team Tags
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Added by content owner
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {/* Show only team/system scoped tags that are applied to this content */}
                {appliedTeamTags.length > 0 && appliedTeamTags.map(tag => (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{
                      backgroundColor: tag.color ? tag.color + '20' : '#ff9800' + '20',
                      borderColor: tag.color || '#ff9800',
                    }}
                  />
                ))}

                {/* Show message if no team tags */}
                {appliedTeamTags.length === 0 && (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    No team tags added to this content yet.
                  </Typography>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Personal Tags */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                My Personal Tags
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Organize this content with your own tags
              </Typography>
              <TagAutoComplete
                appliedTagIds={content.tags || []}
                availableTags={personalTags}
                onTagApply={handleApplyPersonalTag}
                onTagRemove={handleRemovePersonalTag}
                onCreateTag={handleCreatePersonalTag}
                tagScope={'personal' as TagScope}
                organizationId={currentOrganizationId || ''}
                teamId={currentTeamId || ''}
                userId={user?._id || ''}
                placeholder="Type to search, / to browse, or create personal tags..."
                label="Personal Tags"
                maxTags={20}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Ratings & Reviews Section - Right Side */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Ratings & Reviews ({totalRatings || content.ratingCount || 0})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={handleOpenReviewDialog}
              >
                {existingUserRating ? 'Edit Review' : 'Write Review'}
              </Button>
            </Box>

            {/* Overall average rating summary */}
            {displayAvgRating > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: (content as any).totalVersionCount > 1 ? 1 : 2 }}>
                <Rating value={displayAvgRating} precision={0.1} size="small" readOnly />
                <Typography variant="body2" color="text.secondary">
                  {displayAvgRating.toFixed(1)} overall
                </Typography>
              </Box>
            )}

            {/* Version-specific average — shown when content has multiple versions */}
            {(content as any).totalVersionCount > 1 && (() => {
              const vNum = viewingVersion?.versionNumber ?? (content as any).currentVersionNumber;
              if (!vNum || vNum <= 0) return null;
              const versionRatings = [...ratings, ...(existingUserRating ? [existingUserRating] : [])]
                .filter(r => r.versionNumber === vNum);
              if (versionRatings.length === 0) return null;
              const vAvg = versionRatings.reduce((sum, r) => sum + r.rating, 0) / versionRatings.length;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Rating value={vAvg} precision={0.1} size="small" readOnly />
                  <Typography variant="body2" color="text.secondary">
                    {vAvg.toFixed(1)} for v{vNum} ({versionRatings.length} {versionRatings.length === 1 ? 'review' : 'reviews'})
                  </Typography>
                </Box>
              );
            })()}

            {/* Spacing when no averages shown */}
            {(content.avgRating ?? 0) === 0 && !viewingVersion && <Box sx={{ mb: 2 }} />}

            {/* Show user's own rating first if it exists */}
            {existingUserRating && (
              <Box sx={{ mb: 3, pb: 3, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem', bgcolor: 'primary.main' }}>
                    {user?.name?.charAt(0) || 'Y'}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Your Review
                      </Typography>
                      <Chip label="You" size="small" color="primary" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={existingUserRating.rating} size="small" readOnly />
                      {existingUserRating.versionNumber && existingUserRating.versionNumber > 0 && (
                        <Chip
                          label={`v${existingUserRating.versionNumber}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatRatingDates(existingUserRating.createdAt, existingUserRating.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {existingUserRating.comment && (
                  <Typography variant="body2" sx={{ ml: 5 }}>
                    {existingUserRating.comment}
                  </Typography>
                )}
              </Box>
            )}

            {/* Show other users' ratings */}
            {ratings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                  {existingUserRating ? 'No other reviews yet.' : 'No reviews yet. Be the first to rate this content!'}
                </Typography>
              </Box>
            ) : (
              <>
                {ratings.map((rating) => (
                  <Box key={rating.id} sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem' }}>
                        {rating.userName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {rating.userName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Rating value={rating.rating} size="small" readOnly />
                          {rating.versionNumber && rating.versionNumber > 0 && (
                            <Chip
                              label={`v${rating.versionNumber}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatRatingDates(rating.createdAt, rating.updatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {rating.comment && (
                      <Typography variant="body2" sx={{ ml: 5 }}>
                        {rating.comment}
                      </Typography>
                    )}
                  </Box>
                ))}

                {hasMoreRatings && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button 
                      variant="text" 
                      onClick={handleLoadMoreRatings}
                      disabled={loadingMoreRatings}
                    >
                      {loadingMoreRatings ? 'Loading...' : `Load More Reviews (${totalRatings - ratings.length - (existingUserRating ? 1 : 0)} remaining)`}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
      >
        {hasPermission(PERMISSIONS.CONTENT_UPDATE) && (
          <MenuItem onClick={() => handleAction('edit')}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Edit Content"
              secondary={viewingVersion ? 'Returns to current version' : undefined}
            />
          </MenuItem>
        )}

        <MenuItem onClick={handleOpenAiSummary}>
          <ListItemIcon><AutoAwesomeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>AI Summary</ListItemText>
        </MenuItem>

        {hasPermission(PERMISSIONS.CONTENT_UPDATE) && (
          <MenuItem onClick={() => { setMenuAnchorEl(null); setVersionHistoryOpen(true); }}>
            <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Manage Versions</ListItemText>
          </MenuItem>
        )}

        {hasPermission(PERMISSIONS.CONTENT_DELETE) && (
          <>
            <Divider />
            <MenuItem onClick={() => handleAction('delete')}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Delete Content</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* AI Summary Modal */}
      {content && (
        <AiSummaryModal
          open={aiSummaryOpen}
          title={content.title}
          aiSummary={aiSummaryData}
          loading={aiSummaryLoading}
          onClose={() => setAiSummaryOpen(false)}
        />
      )}

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onClose={() => setShowRatingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditingReview ? 'Edit Your Review' : 'Rate this Content'}</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {isEditingReview && (
              <Typography variant="body2" sx={{ mb: 2, p: 1.5, bgcolor: 'info.main', borderRadius: 1, color: 'white' }}>
                You previously rated this content. You can update your rating and review below.
              </Typography>
            )}
            <Typography variant="body1" gutterBottom>
              How would you rate "{displayTitle}"?
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Rating
                value={userRating}
                onChange={(event, newValue) => setUserRating(newValue || 0)}
                size="large"
              />
              <Typography variant="body2" color="text.secondary">
                {userRating > 0 ? `${userRating} star${userRating > 1 ? 's' : ''}` : 'Select rating'}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Review (optional)"
              value={userComment}
              onChange={(e) => setUserComment(e.target.value)}
              placeholder="Share your thoughts about this content..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRatingDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRatingSubmit}
            variant="contained"
            disabled={userRating === 0}
          >
            {isEditingReview ? 'Update Review' : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Content Dialog */}
      {content && (
        <ContentMetadataDialog
          open={showEditDialog}
          initialValues={{
            title: content.title,
            description: content.description,
            status: (content.status as 'draft' | 'private' | 'public' | 'archived') ?? 'draft',
            tagIds: (content as any).tag_ids ?? (content as any).tagIds ?? content.tags ?? [],
            thumbnailUrl: (content as any).thumbnailUrl ?? (content as any).thumbnail_url,
            teamId: (content as any).teamId ?? (content as any).team_id,
            organizationId: (content as any).organizationId ?? (content as any).organization_id,
            aiDescription: (content as any).aiDescription,
            aiSuggestedTitle: (content as any).aiSuggestedTitle,
            suggestedTags: (content as any).suggestedTags,
          }}
          onClose={() => setShowEditDialog(false)}
          onSave={async (values) => {
            await handleSaveContentEdits({
              title: values.title,
              description: values.description,
              status: values.status,
              tag_ids: values.tagIds,
            });
          }}
          kind={((content as any).kind as 'file' | 'view') ?? 'file'}
          contentId={content.id || undefined}
          onEnrichmentTriggered={() => {
            // Optimistically mark as processing — triggers the detail page banner
            // Do NOT close the dialog — it stays open and polls for completion
            setContent(prev => prev ? { ...prev, processingState: 'ai_enrichment_pending', isFullySearchable: false } : prev);
          }}
          onEnrichmentComplete={(_id) => loadContent(true)}
          availableStatuses={['draft', 'private', 'public', 'archived']}
        />
      )}

      {/* Delete Content Dialog */}
      <DeleteContentDialog
        open={showDeleteDialog}
        content={content as any}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteContent}
      />

      {/* Share Dialog */}
      {content && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          itemId={content.id}
          itemType={
            // Derive from kind/mime — views can render through this page if a
            // user lands here via a legacy route; the public link must still
            // carry the correct itemType so the public-link controller looks
            // up the right collection and so PublicLinkAccess renders the
            // correct anonymous flow.
            ((content as any).kind === 'view' || content.mimeType === 'application/vnd.sharp.view')
              ? 'view'
              : 'content'
          }
          itemTitle={content.title}
        />
      )}

      {/* Version History Panel */}
      <VersionHistoryPanel
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        contentId={content.id}
        organizationId={content.organizationId || (content as any).organization_id || ''}
        teamId={content.teamId || (content as any).team_id || currentTeamId || ''}
        currentVersionNumber={(content as any).currentVersionNumber ?? 0}
        canPublish={hasPermission(PERMISSIONS.CONTENT_UPDATE)}
        onVersionPublished={loadContent}
        contentTitle={content.title}
        contentMimeType={content.mimeType || (content as any).mime_type || undefined}
        selectedVersionNumber={viewingVersion?.versionNumber ?? null}
        onVersionSelect={handleVersionSelect}
      />

    </Container>
  );
};

export default ContentDetailPage;